import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { demoModeAPI } from '../services/amidala';

/**
 * Demo Mode toggle for Amidala.
 * - Defaults to OFF (controlled by backend tenant.settings.demo_mode).
 * - When OFF, only findings/accounts/scans from real configured cloud accounts are shown.
 * - When ON, sample/seed data is also visible for demos.
 */
const DemoModeToggle = ({ tenantId, onChange, compact = false }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await demoModeAPI.get(tenantId);
        if (mounted) setEnabled(!!data.enabled);
      } catch (_) {
        if (mounted) setEnabled(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [tenantId]);

  const toggle = async () => {
    if (saving || !tenantId) return;
    const next = !enabled;
    setSaving(true);
    try {
      await demoModeAPI.set(tenantId, next);
      setEnabled(next);
      onChange?.(next);
    } catch (e) {
      console.error('Failed to update demo mode', e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 text-sm text-gray-500" data-testid="demo-mode-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Demo mode…
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      data-testid="demo-mode-toggle"
      className={`inline-flex items-center gap-2 h-10 rounded-full border px-4 text-sm font-medium transition-all ${
        enabled
          ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
          : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
      } ${saving ? 'opacity-60 cursor-wait' : ''} ${compact ? '' : ''}`}
      title={enabled
        ? 'Demo mode is ON — sample data is visible. Click to switch to real data only.'
        : 'Demo mode is OFF — showing only real configured accounts. Click to enable sample data.'}
    >
      <Sparkles className={`w-4 h-4 ${enabled ? 'text-amber-600' : 'text-slate-400'}`} />
      <span>Demo Mode</span>
      <span
        data-testid="demo-mode-status"
        className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold ${
          enabled ? 'bg-amber-500 text-white' : 'bg-slate-300 text-slate-700'
        }`}
      >
        {enabled ? 'ON' : 'OFF'}
      </span>
    </button>
  );
};

export default DemoModeToggle;
